import React, { useEffect, useRef } from 'react';
import {
  PersonOutline,
  ThumbDownAltOutlined,
  ThumbUpAltOutlined,
} from 'Components/Atoms/Icons';
import {
  RESTAURANT_VISIBLE_MESSAGE_TYPES,
  useSupportConversation,
} from 'CustomHooks/Support/useSupportData';
import { RestaurantTicketSummary } from 'types/shared/routes/v03/support';
import MessageBlocks from './MessageBlocks';
import {
  formatSupportTime,
  messageRole,
  roleLabel,
  ticketStatusLabel,
  ticketTypeLabel,
} from './helpers';

type Props = {
  /** null while the conversation only starts with the next message. */
  conversationId: string | null;
  currentUid: string;
  /** All known tickets, used to resolve the conversation's linked tickets. */
  tickets: RestaurantTicketSummary[];
  /** The ticket to show until the conversation (and its own links) is there. */
  pendingTicket?: RestaurantTicketSummary;
  /** The template intro to show until the snapshot brings the stored one. */
  pendingIntro?: string;
  onSelectOption: (value: string) => void;
  onRate: (messageId: string, dir: 'up' | 'down' | null) => void;
  onOpenTicket: (ticketId: string) => void;
  onConnectHuman: () => void;
  /** The button hides only once the snapshot reports the flag, so a second
   * click within that round trip would post the request twice. */
  connectDisabled: boolean;
};

const ChatView = ({
  conversationId,
  currentUid,
  tickets,
  pendingTicket,
  pendingIntro,
  onSelectOption,
  onRate,
  onOpenTicket,
  onConnectHuman,
  connectDisabled,
}: Props) => {
  const { conversation, messages, loading } =
    useSupportConversation(conversationId);
  const endRef = useRef<HTMLDivElement>(null);

  // Defense-in-depth only: the Firestore query (and rule) already exclude
  // internal types/drafts — this stays as a UI safety net, not the actual
  // filter. An allowlist like theirs, so a new internal-only type stays out
  // here too. Soft-deleted messages (admin moderation) are filtered here: the
  // query can't `where('deleted','==',false)` over legacy docs missing the field.
  const visibleMessages = messages.filter(
    (message) =>
      RESTAURANT_VISIBLE_MESSAGE_TYPES.includes(message.type) &&
      !message.draft &&
      !message.deleted,
  );

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [visibleMessages.length]);

  // The panel clears the prop with the first send, the ref outlives it.
  const introRef = useRef<string>();
  if (!conversationId) introRef.current = pendingIntro;
  const intro = visibleMessages.length ? undefined : introRef.current;

  const linkedTickets = conversation
    ? (conversation.tickets || [])
        .map((id) => tickets.find((ticket) => ticket.id === id))
        .filter((ticket): ticket is RestaurantTicketSummary => !!ticket)
    : pendingTicket
      ? [pendingTicket]
      : [];

  // Own vs. Support is decided per message by its `fromCustomer` flag; selfIds
  // is only the fallback for legacy messages written before that field existed.
  const selfIds = [currentUid, conversation?.user?.id].filter(
    Boolean,
  ) as string[];

  const lastVisible = visibleMessages[visibleMessages.length - 1];
  const showConnectHuman =
    !!lastVisible &&
    messageRole(lastVisible.uid, selfIds, lastVisible.fromCustomer) === 'ai' &&
    !conversation?.stopAIFromReplying;

  // Typing indicator: a non-self participant whose `writing` timestamp is fresh.
  const writing = conversation?.writing || {};
  const typingUid = Object.keys(writing).find(
    (id) =>
      !selfIds.includes(id) &&
      typeof writing[id] === 'number' &&
      Date.now() - writing[id] < 60000,
  );
  const typingRole = typingUid ? messageRole(typingUid, selfIds) : null;
  const typingLabel =
    typingRole === 'ai'
      ? 'KI antwortet gerade …'
      : typingRole === 'support'
        ? 'Support schreibt …'
        : null;

  return (
    <div className="support-chat">
      {!!linkedTickets.length && (
        <div className="support-chat__tickets">
          {linkedTickets.map((ticket) => (
            <button
              key={ticket.id}
              type="button"
              className="support-linked-ticket"
              onClick={() => onOpenTicket(ticket.id)}
            >
              <span className="support-linked-ticket__title">
                🎫 {ticket.title}
              </span>
              <span className="support-linked-ticket__meta">
                {ticketTypeLabel(ticket.type)} · {ticketStatusLabel(ticket.status)}
              </span>
            </button>
          ))}
        </div>
      )}

      <div className="support-chat__messages">
        {loading && !visibleMessages.length && !intro && (
          <p className="support-empty">Lädt …</p>
        )}
        {!!intro && (
          <div className="support-message support-message--system">
            <MessageBlocks blocks={[{ type: 'text', text: intro }]} />
          </div>
        )}
        {visibleMessages.map((message) => {
          const role = messageRole(message.uid, selfIds, message.fromCustomer);

          if (role === 'system') {
            return (
              <div
                key={message.id}
                className="support-message support-message--system"
              >
                <MessageBlocks blocks={message.content} />
              </div>
            );
          }

          const isUser = role === 'user';
          const thumb = message.thumb?.dir;

          return (
            <div
              key={message.id}
              className={`support-message support-message--${role}`}
            >
              {!isUser && (
                <span
                  className={`support-message__sender support-message__sender--${role}`}
                >
                  {roleLabel(role)}
                </span>
              )}
              <div
                className={`support-message__bubble${
                  (message.content || []).some(
                    (block) =>
                      block.type === 'text' ||
                      block.type === 'multiple_choice',
                  )
                    ? ' support-message__bubble--filled'
                    : ''
                }`}
              >
                <MessageBlocks
                  blocks={message.content}
                  onSelectOption={isUser ? undefined : onSelectOption}
                />
              </div>
              <div className="support-message__footer">
                <span className="support-message__time">
                  {formatSupportTime(message.createdAt)}
                </span>
                {!isUser && (
                  <span className="support-message__rating">
                    <button
                      type="button"
                      aria-label="Hilfreich"
                      className={`support-thumb${
                        thumb === 'up' ? ' support-thumb--active' : ''
                      }`}
                      onClick={() =>
                        onRate(message.id, thumb === 'up' ? null : 'up')
                      }
                    >
                      <ThumbUpAltOutlined fontSize="inherit" />
                    </button>
                    <button
                      type="button"
                      aria-label="Nicht hilfreich"
                      className={`support-thumb${
                        thumb === 'down' ? ' support-thumb--active' : ''
                      }`}
                      onClick={() =>
                        onRate(message.id, thumb === 'down' ? null : 'down')
                      }
                    >
                      <ThumbDownAltOutlined fontSize="inherit" />
                    </button>
                  </span>
                )}
              </div>
            </div>
          );
        })}
        {typingLabel && (
          <div className="support-typing">
            <span className="support-typing__dots">
              <span />
              <span />
              <span />
            </span>
            {typingLabel}
          </div>
        )}
        {showConnectHuman && (
          <div className="support-connect">
            <button
              type="button"
              className="support-connect__btn"
              disabled={connectDisabled}
              onClick={onConnectHuman}
            >
              <PersonOutline fontSize="inherit" />
              Mit einem Menschen verbinden
            </button>
          </div>
        )}
        <div ref={endRef} />
      </div>
    </div>
  );
};

export default ChatView;
